import { useEffect, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box } from "@mui/material";

import { BeautifulJSON } from "../utils";
import AppAlert from "../../components/AppAlert";
import { RichFormattedMessage } from "../../Configuration/modalInputs/RichFormattedMessage";

export interface AlertItem {
  identifier: string;
  type: "info" | "warning" | "error" | "success";
  code?: string; // Translation key of the message
  closeable?: boolean;

  // message and values
  message: string;
  values?: Record<string, any>;

  // Todo: add collapse functionality
  details?: Record<string, any>;
}

export const createAlert = ({
  identifier,
  type,
  message,
  details,
  code,
  closeable = true,
  values = {},
}: {
  identifier?: string;
  type: "info" | "warning" | "error" | "success";
  message?: string;
  details?: Record<string, any>;
  code?: string;
  closeable?: boolean;
  values?: Record<string, any>;
}): AlertItem => {
  if (!identifier) identifier = crypto.randomUUID();
  if (!message) message = "CONNECTIVITY.ALERTS.UNKNOWN_ERROR";

  return {
    type: type,
    identifier: identifier,
    message: message,
    details: details,
    code: code,
    closeable: closeable,
    values: values,
  };
};

const AlertDetails = ({ details }: { details: Record<string, any> }) => {
  return (
    <Box sx={{ mt: 2, ml: 4 }}>
      <BeautifulJSON
        data={details}
        onDataChanged={() => {}}
        mode={"read_only"}
      />
    </Box>
  );
};

const Alert = ({
  alert,
  onClose,
}: {
  alert: AlertItem;
  onClose: (identifier: string) => void;
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  return (
    <AppAlert
      severity={alert.type}
      onClose={alert.closeable ? () => onClose(alert.identifier) : undefined}
    >
      {alert.details && (
        <>
          {expanded ? (
            <ArrowDropDownIcon
              sx={{ verticalAlign: "middle" }}
              onClick={() => setExpanded(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <ArrowRightIcon
              sx={{ verticalAlign: "middle" }}
              onClick={() => setExpanded(true)}
              style={{ cursor: "pointer" }}
            />
          )}
        </>
      )}

      <RichFormattedMessage id={alert.message} values={alert.values} />

      {expanded && alert.details && <AlertDetails details={alert.details} />}
    </AppAlert>
  );
};

export const BeautifulAlerts = ({
  alerts,
  updateAlerts,
}: {
  alerts: AlertItem[];
  updateAlerts: (alerts: AlertItem[]) => void;
}) => {
  const [_alerts, setAlerts] = useState<AlertItem[]>(alerts);

  const handleClose = (identifier: string) => {
    const newAlerts = _alerts.filter(
      (alert) => alert.identifier !== identifier
    );
    setAlerts(newAlerts);
    updateAlerts(newAlerts);
  };

  useEffect(() => {
    setAlerts(alerts);
  }, [alerts]);

  return (
    <Box
      sx={{
        display: _alerts.length ? "flex" : "none",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {_alerts.map((alert) => (
        <Alert alert={alert} key={alert.identifier} onClose={handleClose} />
      ))}
    </Box>
  );
};
